import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import {
  defaultPaymentTypeSubOptions,
  defaultRepetitionOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { showCurrencyByCountry } from '@nxsys/utils';
import { Form } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment, { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppInput from '@nxsys/components/AppInput';

export type PaymentFormValues = {
  description: string;
  endDate: Moment | null;
  includeInCisCalculation: boolean;
  maxRecurrence: string;
  minPaymentPeriod: string;
  paymentCode: string;
  paymentType: Option<string> | null;
  startDate: Moment | null;
  totalPayment: string;
  repetition: Option<string> | null;
  beforeTax: boolean;
  employeePRSICanBeDeducted: boolean;
  employerPRSICanBeDeducted: boolean;
  uscCanBeDeducted: boolean;
  ascCanBeDeducted: boolean;
  pensionSchemaDeductionCanBeDeducted: boolean;
  lptCanBeDeducted: boolean;
  beforeNI: boolean;
  beforePension: boolean;
};

export type PaymentFormRef = {
  submit: () => void;
};

type PaymentFormProps = {
  formRef?: Ref<PaymentFormRef>;
  onSubmit?: (values: PaymentFormValues) => void;
  defaultValues?: Partial<PaymentFormValues>;
  paymentModel?: string;
  hideDates?: boolean;
};

const PaymentForm: React.FC<PaymentFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  paymentModel,
  hideDates,
}) => {
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isCISWorker = isCISPaymentModel(paymentModel);
  const isUmbrellaPAYE = UMBRELLA_PAYE_HYBRID.includes(paymentModel ?? '');

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<PaymentFormValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: PaymentFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {/* <Form.Item
        label={messages['paymentSetting.payment.paymentCode']}
        name="paymentCode"
        required={true}
      >
        <AppInput placeholder={messages['form.autoPopulate']} disabled />
      </Form.Item> */}

      <Form.Item
        label={messages['paymentSetting.payment.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <TextArea placeholder={messages['form.enterDescription']} rows={4} />
      </Form.Item>
      {isIreland && (
        <Form.Item
          label={messages['common.repetition']}
          name="repetition"
          rules={[
            {
              required: true,
              message: messages['form.pleaseSelect'],
            },
          ]}
        >
          <AppSelect
            options={defaultRepetitionOptions}
            placeholder={messages['form.select']}
          />
        </Form.Item>
      )}
      <Form.Item label={messages['common.paymentType']} name="paymentType">
        <AppSelect
          options={defaultPaymentTypeSubOptions}
          placeholder={messages['form.select']}
        />
      </Form.Item>
      <Form.Item
        label={messages['paymentSetting.payment.minPaymentPeriod']}
        name="minPaymentPeriod"
      >
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      {!hideDates && (
        <>
          <Form.Item label={messages['common.startDate']} name="startDate">
            <AppDatePicker
              placeholder={messages['form.selectDate']}
              format="DD/MM/YYYY"
              maxDate={
                watchValues?.endDate ? moment(watchValues.endDate) : undefined
              }
            />
          </Form.Item>
          <Form.Item label={messages['common.endDate']} name="endDate">
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
              minDate={
                watchValues?.startDate
                  ? moment(watchValues.startDate)
                  : undefined
              }
            />
          </Form.Item>
        </>
      )}
      <Form.Item
        label={messages['paymentSetting.payment.maxRecurrence']}
        name="maxRecurrence"
        rules={[
          {
            pattern: /^\d*$/,
            message:
              messages['form.wholeNumber'] || 'Please enter a whole number',
          },
        ]}
      >
        <AppInput
          placeholder={messages['form.enter']}
          disabled={!!(watchValues?.startDate || watchValues?.endDate)}
        />
      </Form.Item>
      <Form.Item label={messages['common.totalPayment']} name="totalPayment">
        <AppCurrencyInput
          placeholder={messages['form.enter']}
          prefix={showCurrencyByCountry(payrollCountry)}
        />
      </Form.Item>
      {isIreland && (
        <>
          <Form.Item label={messages['common.beforeTax']} name="beforeTax">
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforeEmployeePRSI']}
            name="employeePRSICanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforeEmployerPRSI']}
            name="employerPRSICanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforeUSC']}
            name="uscCanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforeASC']}
            name="ascCanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforePensionSchemeDeductions']}
            name="pensionSchemaDeductionCanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeLPT']}
            name="lptCanBeDeducted"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforeRCT']}
            name="includeInCisCalculation"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </>
      )}
      {!isIreland && isCISWorker && (
        <Form.Item
          label={messages['common.beforeCISTax']}
          name="includeInCisCalculation"
        >
          <AppRadioGroup options={yesNoOptions} />
        </Form.Item>
      )}
      {!isIreland && isUmbrellaPAYE && (
        <>
          <Form.Item label={messages['common.beforeNI']} name="beforeNI">
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item label={messages['common.beforeTax']} name="beforeTax">
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
          <Form.Item
            label={messages['common.beforePension']}
            name="beforePension"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </>
      )}
    </AppFormContainer>
  );
};

export default PaymentForm;
